import { now } from '../Clock.ts';
import type { ButtonEvent, InputDevice } from '../hardware/InputDevice.ts';
import type { StimulationService } from '../stimulation/StimulationService.ts';
import { EventType } from './Events.ts';
import type { EventLogger } from './EventLogger.ts';
import { quantile } from './LatencyTracker.ts';

export interface CalibrationOptions {
  /** How many stimulation trials to run. */
  trials: number;
  /** Nominal gap between trials, ms. */
  interTrialIntervalMs: number;
  /**
   * Random extra delay added to the gap, ms, drawn uniformly from [0, this].
   * Without it a participant anticipates the rhythm and the measured latency is
   * an anticipation time rather than a response time.
   */
  interTrialJitterMs: number;
  /** Give up on a response after this long, ms. */
  responseTimeoutMs: number;
  /** Ignore inputs arriving sooner than this after onset, ms. */
  minValidLatencyMs: number;
  /** Pulse duration requested per trial, ms. Clamped by `SafetyGate`. */
  stimulusDurationMs: number;
  channel: number;
}

export const DEFAULT_CALIBRATION_OPTIONS: CalibrationOptions = {
  trials: 20,
  interTrialIntervalMs: 1500,
  interTrialJitterMs: 700,
  responseTimeoutMs: 800,
  minValidLatencyMs: 20,
  stimulusDurationMs: 60,
  channel: 0,
};

export interface CalibrationTrial {
  index: number;
  requestId: string;
  requestedAt: number;
  stimulusAt: number | null;
  responseAt: number | null;
  /** stimulusAt -> responseAt, ms. Null if missed or rejected. */
  latencyMs: number | null;
  outcome: 'responded' | 'missed' | 'rejected';
  note?: string;
}

export interface CalibrationSummary {
  trials: number;
  responded: number;
  missed: number;
  rejected: number;
  missRate: number;

  medianMs: number;
  meanMs: number;
  p05Ms: number;
  p95Ms: number;
  stdDevMs: number;
  minMs: number;
  maxMs: number;

  /** Suggested `BotParams.latencyMs`: the median response latency. */
  suggestedLatencyMs: number;
  /**
   * Suggested `BotParams.inFlightGraceMs`: p95 - median. Covers the slow tail
   * and writes off anything past it, which is what keeps a missed response from
   * costing the bot half a second of misplaced confidence.
   */
  suggestedGraceMs: number;
  /**
   * Suggested `SafetyGate.minIntervalMs`, and a floor for the bot's own
   * `minIntentIntervalMs`: a participant cannot answer faster than their own
   * refractory period, and requests inside it are stimulation for nothing.
   */
  suggestedMinIntervalMs: number;
}

/**
 * Timing calibration.
 *
 * ## What this measures, and what it does not
 *
 * It measures **timing**: how long after a stimulus the participant's movement
 * is detected, how variable that is, and how often no movement arrives at all.
 *
 * It does **not** touch stimulation intensity, and it must never be extended to.
 * Intensity, electrode placement, and waveform are set by an operator on the
 * hardware, by hand, following the manufacturer's procedure, with the
 * participant present and able to stop. An automated loop that nudged intensity
 * upward until responses became reliable would be exactly the wrong thing to
 * build, and would be indistinguishable in code from this class if this class
 * were allowed to write to the stimulator's parameters. It cannot: there is no
 * intensity field in `StimulusRequest`.
 *
 * ## Procedure
 *
 *   operator arms the system (explicit, outside this class)
 *     -> `start()`
 *     -> for each trial: request one manufacturer-supported stimulus,
 *        wait for a detected input, record the interval
 *     -> inter-trial interval, randomised so responses are not anticipated
 *     -> `summarise()` -> median, spread, miss rate
 *     -> the operator chooses whether to apply the measurement to the bot
 *
 * The measured numbers are *offered*, not applied. `App` puts them behind a
 * button.
 */
export class CalibrationSession {
  readonly options: CalibrationOptions;
  readonly trials: CalibrationTrial[] = [];

  private running = false;
  private awaiting: CalibrationTrial | null = null;
  private nextTrialAt = 0;
  private unsubscribe: (() => void) | null = null;
  private counter = 0;
  private jitterIndex = 0;

  constructor(
    private readonly service: StimulationService,
    private readonly device: InputDevice,
    private readonly logger: EventLogger,
    private readonly getTick: () => number,
    options: Partial<CalibrationOptions> = {},
  ) {
    this.options = { ...DEFAULT_CALIBRATION_OPTIONS, ...options };
  }

  get isRunning(): boolean {
    return this.running;
  }

  get progress(): { done: number; total: number } {
    return { done: this.trials.length, total: this.options.trials };
  }

  /** Currently waiting on a response? Used to light the dashboard indicator. */
  get isAwaitingResponse(): boolean {
    return this.awaiting !== null;
  }

  async start(): Promise<void> {
    if (this.running) return;
    if (!this.service.gate.isArmed) {
      throw new Error('Arm the stimulator before starting calibration');
    }
    if (this.device.getStatus() !== 'connected') {
      await this.device.connect();
    }
    this.trials.length = 0;
    this.counter = 0;
    this.jitterIndex = 0;
    this.running = true;
    this.awaiting = null;
    this.nextTrialAt = now() + 500;
    this.unsubscribe = this.device.onButton((e) => this.onButton(e));
    this.logger.log(EventType.CALIBRATION_START, {
      tick: this.getTick(),
      metadata: { ...this.options, stimulator: this.service.getStatus().kind },
    });
  }

  stop(reason = 'operator'): void {
    if (!this.running) return;
    this.running = false;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.awaiting = null;
    this.logger.log(EventType.CALIBRATION_END, {
      tick: this.getTick(),
      metadata: { reason, ...this.summarise() },
    });
  }

  /**
   * Drive the calibration state machine. Called once per frame, like everything
   * else with a clock in this project.
   */
  pump(nowMs: number): void {
    if (!this.running) return;

    if (this.awaiting) {
      const waited = nowMs - (this.awaiting.stimulusAt ?? this.awaiting.requestedAt);
      if (waited > this.options.responseTimeoutMs) {
        this.awaiting.outcome = 'missed';
        this.recordTrial(this.awaiting);
        this.awaiting = null;
        this.scheduleNext(nowMs);
      }
      return;
    }

    if (this.trials.length >= this.options.trials) {
      this.stop('complete');
      return;
    }

    if (nowMs >= this.nextTrialAt) {
      void this.runTrial(nowMs);
    }
  }

  private async runTrial(nowMs: number): Promise<void> {
    const index = this.counter++;
    const requestId = `cal-${index}`;
    const trial: CalibrationTrial = {
      index,
      requestId,
      requestedAt: nowMs,
      stimulusAt: null,
      responseAt: null,
      latencyMs: null,
      outcome: 'missed',
    };
    // Claim the slot before awaiting, so `pump` does not fire a second trial.
    this.awaiting = trial;

    const result = await this.service.requestStimulus({
      requestId,
      tick: this.getTick(),
      simTimeMs: 0,
      channel: this.options.channel,
      durationMs: this.options.stimulusDurationMs,
      reason: 'calibration',
    });

    if (!result.accepted) {
      trial.outcome = 'rejected';
      trial.note = result.rejectedReason ?? result.error;
      this.recordTrial(trial);
      this.awaiting = null;
      this.scheduleNext(now());
      return;
    }
    trial.stimulusAt = result.startedAt ?? nowMs;
  }

  private onButton(e: ButtonEvent): void {
    if (!e.down || !this.awaiting) return;
    const trial = this.awaiting;
    const onset = trial.stimulusAt;
    if (onset === null) return;

    const latency = e.timestamp - onset;
    if (latency < this.options.minValidLatencyMs) {
      // Too fast to be a response to this stimulus. Counted, not discarded --
      // an anticipatory or spurious press is information about the participant.
      this.logger.log(EventType.INPUT_FALSE_POSITIVE, {
        tick: this.getTick(),
        timestamp: e.timestamp,
        requestId: trial.requestId,
        metadata: { latencyMs: latency, phase: 'calibration' },
      });
      return;
    }

    trial.responseAt = e.timestamp;
    trial.latencyMs = latency;
    trial.outcome = 'responded';
    this.recordTrial(trial);
    this.awaiting = null;
    this.scheduleNext(e.timestamp);
  }

  private recordTrial(trial: CalibrationTrial): void {
    this.trials.push(trial);
    this.logger.log(EventType.CALIBRATION_TRIAL, {
      tick: this.getTick(),
      timestamp: trial.responseAt ?? now(),
      requestId: trial.requestId,
      metadata: {
        index: trial.index,
        outcome: trial.outcome,
        latencyMs: trial.latencyMs,
        note: trial.note,
      },
    });
  }

  private scheduleNext(fromMs: number): void {
    // Deterministic jitter from a fixed low-discrepancy sequence: no RNG state
    // to seed, no anticipation, and the same schedule every session.
    const golden = 0.6180339887;
    const u = (this.jitterIndex++ * golden) % 1;
    this.nextTrialAt = fromMs + this.options.interTrialIntervalMs + u * this.options.interTrialJitterMs;
  }

  summarise(): CalibrationSummary {
    const responded = this.trials.filter((t) => t.outcome === 'responded');
    const latencies = responded
      .map((t) => t.latencyMs as number)
      .sort((a, b) => a - b);

    const n = latencies.length;
    const mean = n ? latencies.reduce((a, b) => a + b, 0) / n : NaN;
    const variance =
      n > 1 ? latencies.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1) : 0;
    const median = quantile(latencies, 0.5);
    const p95 = quantile(latencies, 0.95);

    return {
      trials: this.trials.length,
      responded: n,
      missed: this.trials.filter((t) => t.outcome === 'missed').length,
      rejected: this.trials.filter((t) => t.outcome === 'rejected').length,
      missRate: this.trials.length ? 1 - n / this.trials.length : NaN,
      medianMs: median,
      meanMs: mean,
      p05Ms: quantile(latencies, 0.05),
      p95Ms: p95,
      stdDevMs: Math.sqrt(variance),
      minMs: n ? latencies[0] : NaN,
      maxMs: n ? latencies[n - 1] : NaN,
      suggestedLatencyMs: Number.isFinite(median) ? Math.round(median) : NaN,
      suggestedGraceMs: Number.isFinite(p95 - median) ? Math.max(5, Math.round(p95 - median)) : NaN,
      suggestedMinIntervalMs: Number.isFinite(p95) ? Math.round(p95 + 40) : NaN,
    };
  }
}
